import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";

const NAV = [
  { to: "/", label: "Home", num: "01" },
  { to: "/about-us", label: "About", num: "02" },
  { to: "/#solutions", label: "Solutions", num: "03" },
  { to: "/#industries", label: "Industries", num: "04" },
  { to: "/contact-us", label: "Contact", num: "05" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 border-b border-ink-900 bg-white transition-shadow ${
        scrolled ? "shadow-[0_1px_0_0_#0F0F0F]" : ""
      }`}
    >
      <div className="container-wide flex h-20 items-center justify-between gap-6">
        <Link
          to="/"
          className="flex items-end gap-3"
          aria-label="UMG home"
          data-testid="header-logo-link"
        >
          <Logo className="text-3xl sm:text-4xl" />
          <span className="hidden font-mono text-[10px] uppercase tracking-ultra text-ink-500 sm:inline-block pb-1">
            /// payments
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {NAV.map((item) => (
            <NavItem key={item.label} item={item} />
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <Link to="/apply" className="btn-primary" data-testid="header-cta-get-processing">
            Get Processing →
          </Link>
        </div>

        <button
          type="button"
          className="inline-flex h-11 w-11 items-center justify-center border border-ink-900 bg-white text-ink-900 lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="header-mobile-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-ink-900 bg-white lg:hidden" data-testid="header-mobile-menu">
          <div className="container-wide py-6">
            <div className="flex flex-col">
              {NAV.map((item) => (
                <NavItem key={item.label} item={item} mobile />
              ))}
              <Link
                to="/apply"
                className="btn-primary mt-5 w-full"
                data-testid="mobile-cta-get-processing"
              >
                Get Processing →
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function NavItem({ item, mobile = false }) {
  const isHash = item.to.includes("#");
  const cls = mobile
    ? "flex items-center justify-between border-b border-ink-100 py-4 font-mono text-sm uppercase tracking-wide2 text-ink-900 hover:text-accent"
    : "group inline-flex items-baseline gap-2 font-mono text-xs uppercase tracking-wide2 text-ink-900 transition-colors hover:text-accent";

  const inner = (
    <>
      <span className="font-mono text-[10px] tracking-ultra text-ink-400">{item.num}</span>
      <span>{item.label}</span>
    </>
  );

  if (isHash) {
    return (
      <Link to={item.to} className={cls} data-testid={`nav-${item.label.toLowerCase()}`}>
        {inner}
      </Link>
    );
  }
  return (
    <NavLink
      to={item.to}
      end
      data-testid={`nav-${item.label.toLowerCase()}`}
      className={({ isActive }) =>
        `${cls} ${isActive ? "text-accent" : ""}`
      }
    >
      {inner}
    </NavLink>
  );
}
